import {
  Activity,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { liveQuery } from "dexie";
import {
  BookOpen,
  House,
  Layers3,
  BarChart3,
  Settings2,
  Plus,
  ArrowRight,
  Clock3,
  ChevronRight,
  WifiOff,
  RotateCcw,
  Sun,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { db, initialize, snapshot, updateSettings } from "./db";
import {
  dayKey,
  dateLabel,
  examplesFor,
  durationLabel,
  type Snapshot,
  type WordSense,
} from "./domain";
import { analytics } from "./analytics";
import { buildQueue } from "./study";
import { applyTheme, installOffline } from "./platform";
import { IconButton, Panel, Stat } from "./components";
import Editor from "./Editor";
import Library, { WordDetail, type LibraryFilter } from "./Library";
import Study from "./StudyPage";
const Insights = lazy(() => import("./Insights"));
const Settings = lazy(() => import("./Settings"));

const navigation = [
  { id: "today", label: "Bugün", icon: House },
  { id: "study", label: "Çalış", icon: Layers3 },
  { id: "library", label: "Kelimeler", icon: BookOpen },
  { id: "insights", label: "Analiz", icon: BarChart3 },
];
const titles: Record<string, string> = {
  today: "Bugün",
  study: "Kelime çalış",
  reviews: "Tekrarlar",
  library: "Kelimelerim",
  insights: "Gelişimin",
  settings: "Ayarlar",
  word: "Kelime ayrıntısı",
};
function current() {
  return location.hash.slice(1) || "today";
}
function routeId(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return "";
  }
}
export default function App() {
  const [s, setS] = useState<Snapshot | null>(null),
    [fatal, setFatal] = useState(""),
    [route, setRoute] = useState(current),
    [edit, setEdit] = useState<WordSense | "new" | null>(null),
    [toast, setToast] = useState(""),
    [online, setOnline] = useState(navigator.onLine),
    [offline, setOffline] = useState("Çevrimdışı kullanım hazırlanıyor…"),
    [update, setUpdate] = useState<ServiceWorkerRegistration | null>(null),
    [filter, setFilter] = useState<LibraryFilter>("all"),
    [libraryKey, setLibraryKey] = useState(0),
    [studyKind, setStudyKind] = useState<"daily" | "due" | "difficult">("daily"),
    [clock, setClock] = useState(Date.now());
  const main = useRef<HTMLElement>(null),
    started = useRef(false),
    page = route.split("/")[0],
    known = titles[page] ? page : "today";
  useEffect(() => {
    let cancelled = false;
    let sub: { unsubscribe: () => void } | undefined;
    initialize()
      .then(() => {
        if (!cancelled)
          sub = liveQuery(() => snapshot()).subscribe({
            next: (value) => setS(value),
            error: (e) =>
              setFatal(
                "Yerel veriler açılamadı. Tarayıcıda özel gezinmeyi kapatıp yeniden dene. " +
                  String(e),
              ),
          });
      })
      .catch((e) => setFatal(String(e)));
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, []);
  useEffect(() => {
    const handler = () => setRoute(current());
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);
  useEffect(() => {
    if (!s) return;
    applyTheme(s.settings);
    const media = matchMedia("(prefers-color-scheme: dark)"),
      f = () => applyTheme(s.settings);
    media.addEventListener("change", f);
    return () => media.removeEventListener("change", f);
  }, [s?.settings]);
  useEffect(() => {
    const f = () => setOnline(navigator.onLine);
    window.addEventListener("online", f);
    window.addEventListener("offline", f);
    const t = setInterval(() => setClock(Date.now()), 15000);
    return () => {
      window.removeEventListener("online", f);
      window.removeEventListener("offline", f);
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void installOffline(setOffline, setUpdate);
    if ("speechSynthesis" in window) speechSynthesis.getVoices();
  }, []);
  useEffect(() => {
    document.title = (titles[known] ?? "Sentence") + " — Sentence";
    if (s) {
      requestAnimationFrame(() => {
        const h =
          main.current?.querySelector<HTMLElement>('[data-page-active="true"] h1') ??
          main.current?.querySelector<HTMLElement>("h1");
        h?.focus({ preventScroll: true });
      });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [route, !!s]);
  const notify = useCallback((m: string) => setToast(m), []);
  function go(id: string) {
    if (location.hash === "#" + id) setRoute(id);
    else location.hash = id;
  }
  function openWord(w: WordSense) {
    go("word/" + encodeURIComponent(w.id));
  }
  function openLibrary(f: LibraryFilter = "all") {
    setFilter(f);
    setLibraryKey((k) => k + 1);
    go("library");
  }
  function start(kind: "daily" | "due" | "difficult" = "daily") {
    setStudyKind(kind);
    go(kind === "due" ? "reviews" : "study");
  }
  if (fatal)
    return (
      <main className="fatal">
        <h1>Yerel veriler açılamadı</h1>
        <p>{fatal}</p>
        <p>
          Site verilerini silmeden yeniden yükle. Kaydettiğin bir JSON yedeği varsa
          uygulama açıldıktan sonra Ayarlar’dan geri yükleyebilirsin.
        </p>
        <button onClick={() => location.reload()}>Yeniden dene</button>
      </main>
    );
  if (!s)
    return (
      <main className="loading" aria-busy="true">
        <span className="wordmark">
          Sentence<span>.</span>
        </span>
        <p role="status">Kelime defterin açılıyor…</p>
        <div className="skeleton" />
        <div className="skeleton short" />
      </main>
    );
  const selectedWord =
    page === "word"
      ? s.words.find((w) => w.id === routeId(route.slice(5)) && !w.deleted)
      : undefined;
  return (
    <>
      <a
        href="#main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          main.current?.focus();
        }}
      >
        İçeriğe geç
      </a>
      <div className="app-shell">
        <aside className="sidebar">
          <a className="wordmark" href="#today" aria-label="Sentence ana sayfa">
            Sentence<span>.</span>
          </a>
          <p className="sidebar-subtitle">Cümlelerle İngilizce.</p>
          <nav aria-label="Ana menü">
            {navigation.map((n) => (
              <a
                key={n.id}
                href={"#" + n.id}
                aria-current={
                  known === n.id || (known === "word" && n.id === "library")
                    ? "page"
                    : undefined
                }
              >
                <n.icon size={21} />
                {n.label}
              </a>
            ))}
            <a href="#reviews" aria-current={known === "reviews" ? "page" : undefined}>
              <RotateCcw size={21} />
              Tekrarlar
            </a>
          </nav>
          <div className="sidebar-bottom">
            <div className="local-badge">
              <span className="local-dot" />
              Kişisel ve cihazında
            </div>
            <a
              href="#settings"
              aria-current={known === "settings" ? "page" : undefined}
            >
              <Settings2 size={20} />
              Ayarlar
            </a>
          </div>
        </aside>
        <div className="workspace">
          <header className="app-header">
            <a className="wordmark mobile-wordmark" href="#today">
              Sentence<span>.</span>
            </a>
            <span className="desktop-breadcrumb">
              Kelime defterin <span>/</span> {titles[known]}
            </span>
            <div className="header-actions">
              {!online && (
                <span className="offline-badge">
                  <WifiOff size={16} />
                  Çevrimdışı
                </span>
              )}
              <a className="icon-button" href="#settings" aria-label="Ayarları aç">
                <Settings2 size={21} />
              </a>
            </div>
          </header>
          <main ref={main} id="main-content" tabIndex={-1}>
            {update && (
              <div className="notice update-notice">
                <p>Yeni sürüm hazır. Çalışmanı bitirdikten sonra yenileyebilirsin.</p>
                <button
                  className="secondary"
                  onClick={() => {
                    const w = update.waiting;
                    if (w) {
                      navigator.serviceWorker.addEventListener(
                        "controllerchange",
                        () => location.reload(),
                        { once: true },
                      );
                      w.postMessage("ACTIVATE_UPDATE");
                    }
                  }}
                >
                  Yeni sürümü aç
                </button>
              </div>
            )}
            <div data-page-active={known === "today"} hidden={known !== "today"}>
              <Activity mode={known === "today" ? "visible" : "hidden"}>
                <Home
                  s={s}
                  now={clock}
                  onAdd={() => setEdit("new")}
                  onStudy={start}
                  onLibrary={openLibrary}
                  onInsights={() => go("insights")}
                />
              </Activity>
            </div>
            <div data-page-active={known === "library"} hidden={known !== "library"}>
              <Activity mode={known === "library" ? "visible" : "hidden"}>
                <Library
                  key={libraryKey}
                  s={s}
                  initialFilter={filter}
                  onAdd={() => setEdit("new")}
                  onEdit={setEdit}
                  onDetail={openWord}
                  notify={notify}
                />
              </Activity>
            </div>
            <div data-page-active={known === "insights"} hidden={known !== "insights"}>
              <Activity mode={known === "insights" ? "visible" : "hidden"}>
                <Suspense fallback={<p role="status">Analiz açılıyor…</p>}>
                  <Insights s={s} onWord={openWord} />
                </Suspense>
              </Activity>
            </div>
            {(known === "study" || known === "reviews") && (
              <div data-page-active="true">
                <Study
                  key={known + studyKind}
                  s={s}
                  initialKind={known === "reviews" ? "due" : studyKind}
                  onEdit={setEdit}
                  notify={notify}
                  onLibrary={() => openLibrary()}
                />
              </div>
            )}
            {known === "settings" && (
              <div data-page-active="true">
                <Suspense fallback={<p role="status">Ayarlar açılıyor…</p>}>
                  <Settings s={s} notify={notify} offline={offline} />
                </Suspense>
              </div>
            )}
            {known === "word" && selectedWord && (
              <div data-page-active="true">
                <WordDetail
                  s={s}
                  word={selectedWord}
                  onBack={() => go("library")}
                  onEdit={() => setEdit(selectedWord)}
                  notify={notify}
                />
              </div>
            )}
            {known === "word" && !selectedWord && (
              <div className="empty" data-page-active="true">
                <h1>Kelime bulunamadı</h1>
                <p>Bu kelime silinmiş olabilir.</p>
                <button className="secondary" onClick={() => go("library")}>
                  Kütüphaneyi aç
                </button>
              </div>
            )}
            <footer className="app-footer">
              <span>{online ? offline : "Çevrimdışı · veriler cihazında"}</span>
              <a href="#settings">Yedekleme ve gizlilik</a>
            </footer>
          </main>
        </div>
        <nav className="bottom-nav" aria-label="Mobil ana menü">
          {navigation.map((n) => (
            <a
              href={"#" + n.id}
              key={n.id}
              aria-current={
                known === n.id ||
                (known === "reviews" && n.id === "study") ||
                (known === "word" && n.id === "library")
                  ? "page"
                  : undefined
              }
            >
              <n.icon size={22} />
              <span>{n.label}</span>
            </a>
          ))}
        </nav>
      </div>
      <div className={"toast " + (toast ? "shown" : "")}>
        <div role="status">{toast}</div>
        {toast && (
          <button aria-label="Bildirimi kapat" onClick={() => setToast("")}>
            Kapat
          </button>
        )}
      </div>
      {edit && (
        <Editor
          key={edit === "new" ? "new" : edit.id}
          s={s}
          word={edit === "new" ? undefined : edit}
          onClose={() => setEdit(null)}
          onSaved={notify}
        />
      )}
    </>
  );
}

function Home({
  s,
  now,
  onAdd,
  onStudy,
  onLibrary,
  onInsights,
}: {
  s: Snapshot;
  now: number;
  onAdd: () => void;
  onStudy: (kind?: "daily" | "due" | "difficult") => void;
  onLibrary: (f?: LibraryFilter) => void;
  onInsights: () => void;
}) {
  const a = useMemo(() => analytics(s, 30, new Date(now)), [s, now]),
    queue = buildQueue(s, s.settings.mode, "daily", new Date(now)),
    hour = new Date(now).getHours(),
    greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar",
    remaining = Math.max(0, s.settings.dailyNew - a.newToday),
    progress = Math.min(1, a.newToday / s.settings.dailyNew),
    words = s.words.filter((w) => w.active && !w.deleted),
    hasWords = words.length > 0;
  const feature = words.find((w) => w.lemma === "mistake") ?? words[0],
    ex = feature ? examplesFor(s, feature.id)[0] : undefined;
  const backupDue =
    !s.settings.lastBackup || now - Date.parse(s.settings.lastBackup) > 7 * 86400000;
  return (
    <div className="home stack">
      <div className="page-heading">
        <div className="today-date">
          {new Intl.DateTimeFormat("tr-TR", {
            weekday: "long",
            day: "numeric",
            month: "long",
          }).format(now)}
        </div>
        <h1 tabIndex={-1}>{greeting}.</h1>
        <p>
          {hasWords
            ? `Günlük hedefinde ${remaining} yeni birim kaldı. Zamanı gelen ${a.due.length} tekrar kartın var.`
            : "İngilizcen, kendi cümlelerinle gelişsin."}
        </p>
      </div>
      {!s.settings.onboarded && (
        <div className="welcome-note">
          <div>
            <strong>Kendi kelimelerinle, kendi hızında.</strong>
            <p>
              Fielse’den bir kelime ekle. Anlamını cümlede hatırla; tekrar zamanını
              Sentence planlasın.
            </p>
          </div>
          <button
            className="text-button"
            onClick={() => void updateSettings({ onboarded: true })}
          >
            Anladım
            <Check size={16} />
          </button>
        </div>
      )}
      <section className="daily-card">
        <div className="daily-content">
          <span className="eyebrow">Günlük çalışma planın</span>
          <h2>
            {hasWords ? "Biraz hatırla. Biraz ilerle." : "Bugün bir kelimeyle başla."}
          </h2>
          <p>
            {hasWords
              ? "Zamanı gelen tekrarlarınla başla, ardından yeni cümlelere geç."
              : "Kelimeyi tek başına ezberlemek yerine, kullanıldığı cümleyle birlikte öğren."}
          </p>
          <button className="primary" onClick={hasWords ? () => onStudy() : onAdd}>
            {hasWords
              ? queue.length
                ? "Çalışmaya başla"
                : "Çalışma planını aç"
              : "İlk kelimemi ekle"}
            {hasWords ? <ArrowRight size={18} /> : <Plus size={19} />}
          </button>
          <span className="daily-caption">
            {hasWords
              ? `${queue.length} kart hazır · hedef ${s.settings.dailyNew} yeni birim`
              : "Hazır paket eklemek zorunlu değil."}
          </span>
        </div>
        <div
          className="goal-ring"
          style={{ "--progress": progress * 360 + "deg" } as React.CSSProperties}
          role="img"
          aria-label={`Günlük yeni kelime hedefi: ${a.newToday} / ${s.settings.dailyNew}`}
        >
          <div>
            <strong>
              {a.newToday}
              <span>/{s.settings.dailyNew}</span>
            </strong>
            <small>yeni birim</small>
          </div>
        </div>
      </section>
      <div className="stats-grid home-stats">
        <Stat
          label="Tekrar bekleyen"
          value={a.due.length}
          detail={
            a.overdue.length
              ? `${a.overdue.length} gecikmiş kart`
              : "Kartın zamanı geldiğinde görünür"
          }
          onClick={() => onStudy("due")}
        />
        <Stat
          label="Bugünkü çalışma"
          value={durationLabel(a.todayMs)}
          detail={`${s.settings.dailyMinutes} dakika hedefin`}
          onClick={onInsights}
        />
        <Stat
          label="Çalışma serisi"
          value={a.currentStreak + " gün"}
          detail={
            a.longestStreak ? `En uzun ${a.longestStreak} gün` : "İlk çalışmanla başlar"
          }
          onClick={onInsights}
        />
      </div>
      <div className="home-grid">
        <Panel title="Sıradaki adımın">
          <ol className="plan-list">
            <li>
              <button onClick={() => onStudy("due")}>
                <span className="step-symbol">
                  <RotateCcw size={20} />
                </span>
                <span>
                  <strong>Hatırladıklarını tazele</strong>
                  <small>{a.due.length} zamanı gelen tekrar kartı</small>
                </span>
                <ChevronRight size={18} />
              </button>
            </li>
            <li>
              <button onClick={hasWords ? () => onStudy() : onAdd}>
                <span className="step-symbol">
                  <Plus size={21} />
                </span>
                <span>
                  <strong>Yeni kelimelere yer aç</strong>
                  <small>Bugünkü hedefinden {remaining} birim kaldı</small>
                </span>
                <ChevronRight size={18} />
              </button>
            </li>
            <li>
              <button onClick={() => onStudy("difficult")}>
                <span className="step-symbol">
                  <Layers3 size={20} />
                </span>
                <span>
                  <strong>Zorlandıklarına dön</strong>
                  <small>{a.difficult.size} anlam kaydı</small>
                </span>
                <ChevronRight size={18} />
              </button>
            </li>
          </ol>
        </Panel>
        <section className="sentence-preview">
          <span className="eyebrow">
            {ex ? "Kelime defterinden" : "Cümleyle öğrenmek"}
          </span>
          <p lang="en" className="preview-quote">
            {ex ? (
              ex.en
            ) : (
              <>
                I made a <u>mistake</u> yesterday.
              </>
            )}
          </p>
          <p>{ex ? ex.tr : "Dün bir hata yaptım."}</p>
          <div className="section-heading">
            <span>
              {feature ? feature.lemma : "mistake"}{" "}
              <span className="muted">· {feature ? feature.meaning : "hata"}</span>
            </span>
            <button className="text-button" onClick={() => onLibrary()}>
              {ex ? "Kütüphaneyi aç" : "Örnekleri gör"}
              <ArrowUpRight size={16} />
            </button>
          </div>
        </section>
      </div>
      <section className="add-strip">
        <div>
          <h2>Bugün hangi kelimeyle karşılaştın?</h2>
          <p>Bir kelime, bir anlam ve onu hatırlatacak bir cümle.</p>
        </div>
        <button className="secondary" onClick={onAdd}>
          <Plus size={18} />
          Kelime ekle
        </button>
      </section>
      {hasWords && backupDue && (
        <aside className="backup-reminder">
          <Clock3 size={19} />
          <div>
            <strong>Kelime defterinin bir kopyasını al.</strong>
            <p>JSON yedeğini Dosyalar’a veya iCloud Drive’a kaydedebilirsin.</p>
          </div>
          <a href="#settings">Yedekle</a>
        </aside>
      )}
    </div>
  );
}
